import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Sparkles, Filter } from "lucide-react";
import Layout from "../components/Layout";
import { useSEO } from "../hooks/useSEO";
import {
  beverageCategories,
  categoryOptions,
  manufacturers,
  packagingSuppliers,
  labelSuppliers,
} from "../data/beverageData";

const defaultCategory = "Spirit";

function SelectField({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-white/70">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-white/10 bg-neutral-900 px-4 py-3 text-white outline-none transition focus:border-sky-400"
        data-testid={`select-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {options.map((option) => (
          <option key={option} value={option} className="bg-neutral-900">
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="text-xs uppercase tracking-[0.18em] text-white/40">{label}</div>
      <div className="mt-2 text-lg font-medium">{value}</div>
    </div>
  );
}

function SupplierSection({ title, items }) {
  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="mt-4 grid gap-3">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-white/60">
            No current match. Use guided build for a custom sourcing path.
          </div>
        ) : (
          items.map((item) => (
            <div key={item.name} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <div className="font-medium">{item.name}</div>
              <div className="text-sm text-white/50">{item.country}</div>
              {item.notes && <div className="mt-2 text-sm text-white/65">{item.notes}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function BuildBrand() {
  // Apply SEO meta tags for brand builder / manufacturer sourcing
  useSEO('buildBrand');
  
  const [form, setForm] = useState({
    category: defaultCategory,
    productType: categoryOptions[defaultCategory].productTypes[0],
    flavorFamily: categoryOptions[defaultCategory].flavorFamilies[0],
    manufacturingPath: categoryOptions[defaultCategory].manufacturingPaths[0],
    packagingType: categoryOptions[defaultCategory].packagingTypes[0],
    packagingCountry: "Open",
    labelCountry: "Open",
    labelFormat: "Pressure sensitive",
    positioning: "Premium modern",
    targetUse: "Retail",
  });

  const options = categoryOptions[form.category];

  const matchedManufacturers = useMemo(() => {
    return manufacturers.filter(
      (m) =>
        m.categories.includes(form.category) &&
        m.capabilities.includes(form.manufacturingPath)
    );
  }, [form.category, form.manufacturingPath]);

  const matchedPackaging = useMemo(() => {
    return packagingSuppliers.filter(
      (p) =>
        p.categories.includes(form.category) &&
        p.packagingTypes.includes(form.packagingType) &&
        (form.packagingCountry === "Open" || p.country === form.packagingCountry)
    );
  }, [form.category, form.packagingType, form.packagingCountry]);

  const matchedLabels = useMemo(() => {
    return labelSuppliers.filter(
      (l) =>
        l.categories.includes(form.category) &&
        l.formats.includes(form.labelFormat) &&
        (form.labelCountry === "Open" || l.country === form.labelCountry)
    );
  }, [form.category, form.labelFormat, form.labelCountry]);

  const handleCategoryChange = (category) => {
    const next = categoryOptions[category];
    setForm({
      category,
      productType: next.productTypes[0],
      flavorFamily: next.flavorFamilies[0],
      manufacturingPath: next.manufacturingPaths[0],
      packagingType: next.packagingTypes[0],
      packagingCountry: "Open",
      labelCountry: "Open",
      labelFormat: "Pressure sensitive",
      positioning: "Premium modern",
      targetUse: "Retail",
    });
  };

  const specSheet = useMemo(() => {
    return `
BEVRELAY PRODUCT BRIEF

Category: ${form.category}
Product Type: ${form.productType}
Flavor Family: ${form.flavorFamily}
Manufacturing Path: ${form.manufacturingPath}
Packaging Type: ${form.packagingType}
Packaging Country: ${form.packagingCountry}
Label Country: ${form.labelCountry}
Label Format: ${form.labelFormat}
Positioning: ${form.positioning}
Target Use: ${form.targetUse}

Recommended Manufacturers:
${matchedManufacturers.map((m) => `- ${m.name} (${m.country})`).join("\n") || "- No current match"}

Recommended Packaging Suppliers:
${matchedPackaging.map((p) => `- ${p.name} (${p.country})`).join("\n") || "- No current match"}

Recommended Label Suppliers:
${matchedLabels.map((l) => `- ${l.name} (${l.country})`).join("\n") || "- No current match"}
    `.trim();
  }, [form, matchedManufacturers, matchedPackaging, matchedLabels]);

  const downloadSpecSheet = () => {
    const blob = new Blob([specSheet], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bevrelay-beverage-brief.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const update = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Layout>
      <main className="min-h-screen bg-neutral-950 text-white pt-20">
        <div className="mx-auto max-w-7xl px-6 py-10 md:px-10 lg:px-12">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm uppercase tracking-[0.2em] text-white/40">Build a Brand</div>
              <h1 className="mt-2 text-4xl font-semibold tracking-tight md:text-5xl">
                Source-to-Shelf Beverage Builder
              </h1>
              <p className="mt-4 max-w-3xl text-lg text-white/70">
                Build any spirit, wine, sparkling, RTD, water, coffee, juice, or functional beverage
                from concept to supplier path.
              </p>
            </div>

            <Link
              to="/request-access"
              className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium transition hover:bg-white/10"
              data-testid="request-guided-build-btn"
            >
              Request Guided Build
            </Link>
          </div>

          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <Filter className="h-5 w-5 text-sky-300" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Builder Inputs</h2>
                  <p className="text-sm text-white/50">Choose category, liquid type, sourcing path, and packaging.</p>
                </div>
              </div>

              <div className="grid gap-4">
                <SelectField
                  label="Category"
                  value={form.category}
                  options={beverageCategories}
                  onChange={handleCategoryChange}
                />

                <SelectField
                  label="Product Type"
                  value={form.productType}
                  options={options.productTypes}
                  onChange={(v) => update("productType", v)}
                />

                <SelectField
                  label="Flavor Family"
                  value={form.flavorFamily}
                  options={options.flavorFamilies}
                  onChange={(v) => update("flavorFamily", v)}
                />

                <SelectField
                  label="Manufacturing Path"
                  value={form.manufacturingPath}
                  options={options.manufacturingPaths}
                  onChange={(v) => update("manufacturingPath", v)}
                />

                <SelectField
                  label="Packaging Type"
                  value={form.packagingType}
                  options={options.packagingTypes}
                  onChange={(v) => update("packagingType", v)}
                />

                <SelectField
                  label="Packaging Country"
                  value={form.packagingCountry}
                  options={["Open", "Mexico", "USA", "France", "Italy", "China", "Canada", "UK"]}
                  onChange={(v) => update("packagingCountry", v)}
                />

                <SelectField
                  label="Label Country"
                  value={form.labelCountry}
                  options={["Open", "Mexico", "USA", "Canada", "UK"]}
                  onChange={(v) => update("labelCountry", v)}
                />

                <SelectField
                  label="Label Format"
                  value={form.labelFormat}
                  options={["Pressure sensitive", "Shrink sleeve", "Necker", "Hang tag"]}
                  onChange={(v) => update("labelFormat", v)}
                />

                <SelectField
                  label="Positioning"
                  value={form.positioning}
                  options={["Premium modern", "Luxury", "Farm-to-table", "Mass premium", "Wellness-forward", "Cocktail-driven"]}
                  onChange={(v) => update("positioning", v)}
                />

                <SelectField
                  label="Target Use"
                  value={form.targetUse}
                  options={["Retail", "On-premise", "Retail + on-premise", "DTC", "Hospitality"]}
                  onChange={(v) => update("targetUse", v)}
                />
              </div>
            </section>

            <section className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.03] p-6">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <Sparkles className="h-5 w-5 text-emerald-300" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Recommended Build Path</h2>
                  <p className="text-sm text-white/50">Dynamic matches based on category and build choices.</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <InfoCard label="Category" value={form.category} />
                <InfoCard label="Type" value={form.productType} />
                <InfoCard label="Flavor" value={form.flavorFamily} />
              </div>

              <SupplierSection title="Suggested Manufacturers" items={matchedManufacturers} />
              <SupplierSection title="Suggested Packaging Suppliers" items={matchedPackaging} />
              <SupplierSection title="Suggested Label Suppliers" items={matchedLabels} />

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={downloadSpecSheet}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 font-medium text-neutral-950 transition hover:brightness-95"
                  data-testid="download-spec-btn"
                >
                  <Download className="h-4 w-4" />
                  Download Spec Sheet
                </button>

                <Link
                  to="/request-access"
                  className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-medium transition hover:bg-white/10"
                  data-testid="request-introductions-btn"
                >
                  Request Supplier Introductions
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>
    </Layout>
  );
}
